import { Sheet, SheetContent, SheetTitle, Stack, Text } from "@ferrivia/ui";
import { useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import { useAppContext } from "../../../app/useAppContext";
import { useDismissOnBack } from "../../../hooks/useDismissOnBack";
import { SYDANKESKUS_BOOKING_URL } from "../../../lib/bookingLinks";
import s from "./BookingSheet.module.css";

/** What the remote appointment covers, what it does not promise, and the
    outbound link that books it. Lifted out of the readyToBookCta card the
    infusion steps card replaced — the sheet is the same, only what opens it
    moved. */
export function BookingSheet({
  open,
  onOpenChange,
  container,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where the sheet portals, when the caller is itself a fixed overlay that
      would otherwise cover it. Defaults to the app shell's root. */
  container?: HTMLElement | null;
}) {
  const { t } = useTranslation("client");
  const tracker = useTracker();
  const { rootElement } = useAppContext();

  useDismissOnBack(open, () => onOpenChange(false), "infusionBooking");

  const handleBookClick = () => {
    tracker.track("infusion.step_cta_clicked", { cta: "book" });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent container={container ?? rootElement}>
        <SheetTitle>{t("infusion.steps.booking.sheetTitle", "Etävastaanotto lääkärille")}</SheetTitle>
        <Stack gap="4">
          <Text as="p" size="sm">
            {t(
              "infusion.steps.booking.sheetIntro",
              "Etävastaanotolla lääkäri käy läpi esitietosi ja verikoketuloksesi ja keskustelee kanssasi rautainfuusiosta.",
            )}
          </Text>
          <Text as="p" size="sm">
            {t(
              "infusion.steps.booking.sheetDisclaimer",
              "Vastaanotto ei ole tae infuusiosta — lääkäri päättää hoidon sopivuudesta yksilöllisesti.",
            )}
          </Text>
          <Text as="p" size="sm">
            {t(
              "infusion.steps.booking.sheetInstructions",
              "Varaa aika alta Sydänkeskuksen ajanvarauksesta. Valitse etävastaanotto ja käytä omia yhteystietojasi varausta tehdessäsi.",
            )}
          </Text>
          {/* A plain anchor, not Button: this has to be a real outbound link,
              not a JS navigation. */}
          <a
            className={s.cta}
            href={SYDANKESKUS_BOOKING_URL}
            target="_blank"
            rel="noreferrer"
            onClick={handleBookClick}
          >
            {t("infusion.steps.booking.sheetCta", "Varaa aika Sydänkeskuksesta")}
          </a>
        </Stack>
      </SheetContent>
    </Sheet>
  );
}
